import { IBaseResponseModel } from "@/app/models/types/base-response-model";
import { apiService } from "../axios/axios-instance";
import { API_ENDPOINTS } from "../endpoints/api-endpoints";

// Types for vacancy data
export interface Vacancy {
  uuid: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience_level: string | null;
  salary_range: string;
  description: string;
  requirements: string;
  responsibilities: string;
  benefits: string;
  status: number;
  status_label: string;
  application_deadline: string;
  positions_available: number;
  applications_received: number;
  created_at: string;
  updated_at: string;
}

// Helper function to capitalize first letter of type
export const getCapitalizedType = (vacancy: Vacancy): string => {
  if (!vacancy.type) return "Full-time";
  return vacancy.type.charAt(0).toUpperCase() + vacancy.type.slice(1).toLowerCase();
};

export interface Pagination {
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

export interface VacanciesResponseData {
  vacancies: Vacancy[];
  pagination: Pagination;
}

export interface DepartmentsResponseData {
  departments: string[];
}

// Alternative structure - departments might be directly in the response
export type DepartmentsResponse = IBaseResponseModel<string[]> | IBaseResponseModel<DepartmentsResponseData>;
export type VacanciesResponse = IBaseResponseModel<VacanciesResponseData>;

export interface GetVacanciesParams {
  department?: string;
  skip?: number;
  limit?: number;
  status?: string;
}

export class VacancyApi {
  // Get unique departments
  static async getDepartments(): Promise<DepartmentsResponse> {
    return apiService.get<DepartmentsResponseData>(API_ENDPOINTS.VACANCY.GET_VACANCY_UNIQUE_DEPARTMENT);
  }

  // Get vacancies with optional department filter
  static async getVacancies(params?: GetVacanciesParams): Promise<VacanciesResponse> {
    return apiService.get<VacanciesResponseData>(API_ENDPOINTS.VACANCY.GET_VACANCIES, params);
  }

  // Get vacancy details by UUID
  static async getVacancyDetails(uuid: string): Promise<IBaseResponseModel<Vacancy>> {
    try {
      // Make sure the UUID is properly formatted
      if (!uuid || typeof uuid !== 'string') {
        throw new Error(`Invalid UUID: ${uuid}`);
      }

      return apiService.get<Vacancy>(`${API_ENDPOINTS.VACANCY.GET_VACANCY_DETAILS}/${uuid}`, { status: "1" });
    } catch (error) {
      console.error('Error fetching vacancy details:', error);
      throw error;
    }
  }

  // Apply for a vacancy
  static async applyForVacancy(formData: FormData): Promise<IBaseResponseModel<any>> {
    try {
      // Use apiService for consistency with other API calls
      return await apiService.post(API_ENDPOINTS.VACANCY.APPLY_VACANCY, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Error applying for vacancy:', error);
      throw error;
    }
  }
}
